import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, ClipboardList, FlaskConical, GripVertical, MoreVertical, Plus, Sparkles } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { Category, GeneratedContent, GeneratedCoursePlan, KnowledgeModule, KnowledgeTopic, LabListItem, ModuleAssessment, ProjectListItem, RelatedLab, RelatedProject } from '../../types/content';
import Button from '../../components/UI/Button';
import ActionStatus from '../../components/UI/ActionStatus';
import Input from '../../components/UI/Input';
import CourseEnrollmentAdminPanel from './CourseEnrollmentAdminPanel';
import AuthorCard from '../../components/content/AuthorCard';
import BlockEditor, { emptyBlock } from '../../components/content/BlockEditor';
import AssessmentFlowEditor from '../../components/content/AssessmentFlowEditor';
import CatalogBrowseModal from '../../components/content/CatalogBrowseModal';
import {
  CurriculumPreview,
  moduleStripeClass,
  topicStripeClass,
  toggleExclusiveKey,
  toggleId,
} from '../../components/content/CurriculumCollapse';
import LoadingOverlay, { PageLoading } from '../../components/UI/LoadingOverlay';
import {
  ConfirmChangesDialog,
  UnsavedLeaveDialog,
  useDirtyBaseline,
  useUnsavedChangesGuard,
} from '../../components/content/UnsavedLeaveDialog';
import { useAutoClear } from '../../hooks/useAutoClear';
import { useAuth } from '../../context/AuthContext';
import { submitMessage } from '../../utils/roles';
import { confirmAction as askConfirm } from '../../utils/confirmAction';
import { moduleFlow, nextFlowOrder, stripLeadingSequence } from '../../utils/moduleFlow';
import ContentOwnerSelect from '../../components/content/ContentOwnerSelect';
import { focusPageTop } from '../../utils/focusPageTop';
import { openLearnerTab } from '../../utils/openLearnerTab';
import { getUserProfileId } from '../../utils/userProfile';

const emptyTopic = (order: number): KnowledgeTopic => ({
  title: '',
  slug: '',
  objective: '',
  estimatedMinutes: 12,
  displayOrder: order,
  blocks: [emptyBlock(1)],
  relatedLabs: [],
  references: [],
});

const emptyAssessment = (order: number): ModuleAssessment => ({
  title: '',
  description: '',
  passingScore: 70,
  displayOrder: order,
  questions: [{ prompt: '', explanation: '', displayOrder: 1, options: [
    { text: '', isCorrect: true, displayOrder: 1 },
    { text: '', isCorrect: false, displayOrder: 2 },
  ] }],
});

const emptyModule = (order: number): KnowledgeModule => ({
  title: '',
  goal: '',
  displayOrder: order,
  linkOnly: false,
  topics: [emptyTopic(1)],
  assessments: [],
});

type Selection =
  | { kind: 'details' }
  | { kind: 'module'; moduleIndex: number }
  | { kind: 'topic'; moduleIndex: number; topicIndex: number }
  | { kind: 'assessment'; moduleIndex: number; assessmentIndex: number };

type DragPayload =
  | { type: 'module'; index: number }
  | { type: 'topic'; moduleIndex: number; topicIndex: number }
  | { type: 'flow'; moduleIndex: number; flowIndex: number };

type ContextMenu =
  | { kind: 'module-add' }
  | { kind: 'module'; moduleIndex: number }
  | { kind: 'topic'; moduleIndex: number; topicIndex: number }
  | null;

const parseDrag = (raw: string): DragPayload | null => {
  const parts = raw.split(':');
  if (parts[0] === 'module' && parts.length === 2) return { type: 'module', index: Number(parts[1]) };
  if (parts[0] === 'topic' && parts.length === 3) {
    return { type: 'topic', moduleIndex: Number(parts[1]), topicIndex: Number(parts[2]) };
  }
  if (parts[0] === 'flow' && parts.length === 3) {
    return { type: 'flow', moduleIndex: Number(parts[1]), flowIndex: Number(parts[2]) };
  }
  return null;
};

const applyFlowOrders = (module: KnowledgeModule, flow = moduleFlow(module.topics, module.assessments)): KnowledgeModule => {
  const topics = module.topics.map((topic) => ({ ...topic }));
  const assessments = (module.assessments || []).map((assessment) => ({ ...assessment }));
  flow.forEach((item, index) => {
    if (item.kind === 'topic' && topics[item.topicIndex]) {
      topics[item.topicIndex] = { ...topics[item.topicIndex], displayOrder: index + 1 };
    }
    if (item.kind === 'assessment' && assessments[item.assessmentIndex]) {
      assessments[item.assessmentIndex] = { ...assessments[item.assessmentIndex], displayOrder: index + 1 };
    }
  });
  return { ...module, topics, assessments };
};

const emptyOption = (order: number, isCorrect = false) => ({ text: '', isCorrect, displayOrder: order });

const arrayMove = <T,>(list: T[], from: number, to: number) => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

type CourseSavePayload = {
  title: string;
  subtitle: string;
  summary: string;
  learningOutcomes: string;
  prerequisites: string;
  estimatedDurationHours: number;
  displayOrder: number;
  categoryId: number | null;
  difficulty: string;
  isFeatured: boolean;
  price: number;
  discountPercent: number;
  tags: string[];
  projectIds: number[];
  modules: { id?: number; title: string; topics: { id?: number; title: string; blocks: unknown[] }[] }[];
};

const summarizeCourseChanges = (previous: CourseSavePayload | null, next: CourseSavePayload): string[] => {
  const topicCount = (item: CourseSavePayload) => item.modules.reduce((sum, module) => sum + module.topics.length, 0);
  const blockCount = (item: CourseSavePayload) => item.modules.reduce(
    (sum, module) => sum + module.topics.reduce((inner, topic) => inner + (topic.blocks?.length || 0), 0),
    0
  );
  if (!previous) {
    return [
      `New course: ${next.title || '(untitled)'}`,
      `${next.modules.length} module(s), ${topicCount(next)} topic(s), ${blockCount(next)} block(s)`,
    ];
  }
  const lines: string[] = [];
  const field = (label: string, before: string | number | boolean, after: string | number | boolean) => {
    if (before !== after) lines.push(`${label}: ${String(before || '—')} → ${String(after || '—')}`);
  };
  field('Title', previous.title, next.title);
  field('Subtitle', previous.subtitle, next.subtitle);
  field('Summary', previous.summary, next.summary);
  field('Outcomes', previous.learningOutcomes, next.learningOutcomes);
  field('Prerequisites', previous.prerequisites, next.prerequisites);
  field('Duration (hours)', previous.estimatedDurationHours, next.estimatedDurationHours);
  field('Difficulty', previous.difficulty, next.difficulty);
  field('Price', previous.price, next.price);
  field('Discount %', previous.discountPercent, next.discountPercent);
  field('Featured', previous.isFeatured, next.isFeatured);
  if (previous.modules.length !== next.modules.length) {
    lines.push(`Modules: ${previous.modules.length} → ${next.modules.length}`);
  }
  next.modules.forEach((module, index) => {
    const older = previous.modules[index];
    if (!older) {
      lines.push(`Added module: ${module.title || '(untitled)'}`);
      return;
    }
    if (older.title !== module.title) lines.push(`Module renamed: ${older.title || '(untitled)'} → ${module.title || '(untitled)'}`);
    if (older.topics.length !== module.topics.length) {
      lines.push(`${module.title || 'Module'}: ${older.topics.length} → ${module.topics.length} topic(s)`);
    }
    module.topics.forEach((topic, topicIndex) => {
      const olderTopic = older.topics[topicIndex];
      if (!olderTopic) {
        lines.push(`Added topic: ${topic.title || '(untitled)'}`);
        return;
      }
      if (olderTopic.title !== topic.title) lines.push(`Topic renamed: ${olderTopic.title || '(untitled)'} → ${topic.title || '(untitled)'}`);
      if ((olderTopic.blocks?.length || 0) !== (topic.blocks?.length || 0)) {
        lines.push(`${topic.title || 'Topic'} blocks: ${olderTopic.blocks?.length || 0} → ${topic.blocks?.length || 0}`);
      }
    });
  });
  previous.modules.slice(next.modules.length).forEach((module) => {
    lines.push(`Removed module: ${module.title || '(untitled)'}`);
  });
  if (!lines.length) lines.push('No listed field changes. Save the current draft as-is?');
  return lines;
};

const CourseEditorPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const courseId = id ? Number(id) : undefined;
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [summary, setSummary] = useState('');
  const [outcomes, setOutcomes] = useState('');
  const [prerequisites, setPrerequisites] = useState('');
  const [hours, setHours] = useState(40);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [difficulty, setDifficulty] = useState('Beginner');
  const [tags, setTags] = useState('');
  const [coverImageId, setCoverImageId] = useState<number | undefined>();
  const [coverImageUrl, setCoverImageUrl] = useState<string | undefined>();
  const [featured, setFeatured] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [unlimitedSeats, setUnlimitedSeats] = useState(false);
  const [maxSeats, setMaxSeats] = useState(20);
  const [changelog, setChangelog] = useState<{ action: string; summary?: string | null; changedBy?: string | null; changedByRole?: string | null; dateCreated: string }[]>([]);
  const [price, setPrice] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [authorId, setAuthorId] = useState<number | ''>('');
  const [authorLabel, setAuthorLabel] = useState('');
  const [projectIds, setProjectIds] = useState<number[]>([]);
  const [availableProjects, setAvailableProjects] = useState<ProjectListItem[]>([]);
  const [modules, setModules] = useState<KnowledgeModule[]>([emptyModule(1)]);
  const [openModuleIds, setOpenModuleIds] = useState<number[]>([0]);
  const [openTopicKeys, setOpenTopicKeys] = useState<string[]>(['0:0']);
  const [openTocModules, setOpenTocModules] = useState<number[]>([0]);
  const [selection, setSelection] = useState<Selection>({ kind: 'details' });
  const [pendingModuleFocus, setPendingModuleFocus] = useState<number | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [courseJobId, setCourseJobId] = useState<string | null>(null);
  const [courseStage, setCourseStage] = useState<string | null>(null);
  const applyCourseRef = useRef<(plan: GeneratedCoursePlan) => void>(() => undefined);
  const applyingCourse = useRef(false);
  const [error, setError] = useState<string | null>(null);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 10000);
  const [aiTopic, setAiTopic] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [availableLabs, setAvailableLabs] = useState<LabListItem[]>([]);
  const [loading, setLoading] = useState(!!courseId);
  const [dragging, setDragging] = useState<string | null>(null);
  const [browse, setBrowse] = useState<'module' | 'lab' | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenu>(null);
  const [aiPanel, setAiPanel] = useState<'module' | 'topic' | 'review' | 'assessment' | 'course' | null>(null);
  const [confirmAction, setConfirmAction] = useState<'draft' | 'review' | null>(null);
  const [reviewNotes, setReviewNotes] = useState<string | null>(null);
  const [workflowStatus, setWorkflowStatus] = useState<string | null>(null);
  const [courseSlug, setCourseSlug] = useState('');

  useEffect(() => {
    contentApi.listCategories('Course').then((r) => setCategories(r.data.categories || [])).catch(() => undefined);
    Promise.all([
      contentApi.myProjects().catch(() => ({ data: { projects: [] as ProjectListItem[] } })),
      contentApi.listProjects({ pageSize: 50 }).catch(() => ({ data: { projects: [] as ProjectListItem[] } })),
    ]).then(([mine, published]) => {
      const map = new Map<number, ProjectListItem>();
      [...(published.data.projects || []), ...(mine.data.projects || [])].forEach((item) => map.set(item.id, item));
      setAvailableProjects([...map.values()]);
    });
  }, []);

  useEffect(() => {
    if (courseId) return;
    contentApi.getCatalogSettings()
      .then((r) => {
        const seats = r.data.settings?.defaultMaxSeats;
        if (seats && seats > 0) setMaxSeats(seats);
      })
      .catch(() => undefined);
  }, [courseId]);

  useEffect(() => {
    if (!courseId) {
      setAvailableLabs([]);
      return;
    }
    contentApi.myLabs({ courseId, pageSize: 100 }).then((r) => setAvailableLabs(r.data.labs || [])).catch(() => undefined);
  }, [courseId]);

  useEffect(() => {
    if (courseId) return;
    const profileId = getUserProfileId(user);
    if (!profileId) return;
    setAuthorId(profileId);
    setAuthorLabel(user?.username || user?.email || '');
  }, [courseId, user]);

  useEffect(() => {
    if (!courseId) return;
    setLoading(true);
    contentApi.getCourse(courseId).then((r) => {
      const a = r.data.course;
      if (!a) return;
      setTitle(a.title);
      setSubtitle(a.subtitle || '');
      setSummary(a.summary || '');
      setOutcomes(a.learningOutcomes || '');
      setPrerequisites(a.prerequisites || '');
      setHours(a.estimatedDurationHours || 0);
      setDisplayOrder(a.displayOrder || 0);
      setCategoryId(a.categoryId || '');
      setDifficulty(a.difficulty);
      setTags(a.tags.join(', '));
      setCoverImageId(a.coverImageId || undefined);
      setCoverImageUrl(a.coverImageUrl || undefined);
      setFeatured(!!a.isFeatured);
      setIsPublic(a.isPublic !== false);
      setUnlimitedSeats(!(a.maxSeats && a.maxSeats > 0));
      setMaxSeats(a.maxSeats && a.maxSeats > 0 ? a.maxSeats : 20);
      setPrice(Number(a.price || 0));
      setDiscountPercent(Number(a.discountPercent || 0));
      setAuthorId(a.author?.authorId || '');
      setAuthorLabel(a.author?.displayName || '');
      setReviewNotes(a.reviewNotes || null);
      setWorkflowStatus(a.status || null);
      setCourseSlug(a.slug || '');
      setProjectIds((a.projects || []).map((p: RelatedProject) => p.id));
      if (a.modules?.length) {
        setModules(a.modules.map((m) => ({
          ...m,
          linkOnly: false,
          assessments: m.assessments || [],
          topics: (m.topics || []).map((t) => ({
            ...t,
            relatedLabs: t.relatedLabs || [],
            references: t.references || [],
            blocks: t.blocks?.length ? t.blocks : [emptyBlock(1)],
          })),
        })));
      } else if (a.sections?.length) {
        setModules([{
          title: 'Curriculum',
          goal: '',
          displayOrder: 1,
          topics: a.sections.map((s, i) => ({
            title: s.title,
            slug: '',
            objective: '',
            estimatedMinutes: 12,
            displayOrder: i + 1,
            blocks: s.blocks,
            relatedLabs: [],
          })),
        }]);
      }
      setOpenModuleIds([0]);
      setOpenTopicKeys(['0:0']);
      setOpenTocModules([0]);
      setSelection({ kind: 'details' });
    }).catch(() => setError('Unable to load course.')).finally(() => setLoading(false));
    contentApi.courseChangelog(courseId).then((r) => setChangelog(r.data.entries || [])).catch(() => undefined);
  }, [courseId]);

  const payload = useMemo(() => ({
    title,
    subtitle,
    summary,
    learningOutcomes: outcomes,
    prerequisites,
    estimatedDurationHours: hours,
    displayOrder,
    categoryId: categoryId || null,
    difficulty,
    coverImageId,
    isFeatured: featured,
    isPublic,
    maxSeats: unlimitedSeats ? 0 : Math.max(1, maxSeats),
    price,
    discountPercent,
    currency: 'INR',
    authorId: authorId || null,
    tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
    projectIds,
    modules: modules.map((m, i) => ({
      ...m,
      displayOrder: i + 1,
      assessments: (m.assessments || []).map((assessment, n) => ({
        ...assessment,
        displayOrder: assessment.displayOrder || n + 1,
        questions: (assessment.questions || []).map((q, qi) => ({
          ...q,
          displayOrder: qi + 1,
          options: (q.options || []).map((o, oi) => ({ ...o, displayOrder: oi + 1 })),
        })),
      })),
      topics: m.topics.map((t, j) => ({
        ...t,
        displayOrder: t.displayOrder || j + 1,
        relatedLabs: (t.relatedLabs || []).map((lab, n) => ({ labId: lab.id, displayOrder: n + 1 })),
        references: (t.references || []).map((ref, n) => ({ ...ref, displayOrder: n + 1 })),
        blocks: t.blocks.map((b, n) => ({ ...b, displayOrder: n + 1 })),
      })),
    })),
  }), [title, subtitle, summary, outcomes, prerequisites, hours, displayOrder, categoryId, difficulty, coverImageId, featured, isPublic, unlimitedSeats, maxSeats, price, discountPercent, authorId, tags, projectIds, modules]);

  const { isDirty, markClean, previous } = useDirtyBaseline(payload, !loading);
  const { leaveOpen, stay, leaveWithoutSaving } = useUnsavedChangesGuard(isDirty);

  const save = async (submit = false) => {
    setSaving(true);
    setError(null);
    try {
      const response = await contentApi.saveCourse(payload, courseId);
      const savedId = response.data.course?.id;
      if (!savedId) throw new Error('Save failed.');
      if (submit) {
        await contentApi.submitCourse(savedId);
        setMessage(submitMessage(user));
      } else {
        setMessage('Draft saved.');
      }
      markClean();
      if (!courseId) navigate(`/my-courses/${savedId}/edit`, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
      setConfirmAction(null);
    }
  };

  const applyGenerated = (content: GeneratedContent, asTopic = false) => {
    const topics = content.sections.map((section, i) => ({
      title: stripLeadingSequence(section.heading || content.title || ''),
      slug: '',
      objective: i === 0 ? (content.objective || '') : '',
      estimatedMinutes: 12,
      displayOrder: i + 1,
      relatedLabs: [] as RelatedLab[],
      references: (content.references || []).filter(Boolean).map((url, n) => ({ title: url, url, displayOrder: n + 1 })),
      blocks: section.blocks.map((block, j) => ({
        type: block.type,
        content: block.content,
        language: block.language,
        displayOrder: j + 1,
      })),
    }));
    if (asTopic && selection.kind !== 'details') {
      const mi = selection.moduleIndex;
      setModules((all) => all.map((module, i) => {
        if (i !== mi) return module;
        const start = nextFlowOrder(module.topics, module.assessments);
        return applyFlowOrders({
          ...module,
          linkOnly: false,
          topics: [...module.topics, ...topics.map((topic, idx) => ({
            ...topic,
            displayOrder: start + idx,
          }))],
        });
      }));
      setSelection({ kind: 'topic', moduleIndex: mi, topicIndex: modules[mi].topics.length });
      setAiPanel(null);
      requestAnimationFrame(() => focusPageTop('course-editor-top'));
      return;
    }
    const nextIndex = modules.length;
    setModules((all) => [...all, {
      title: stripLeadingSequence(content.title || '') || `AI draft ${all.length + 1}`,
      goal: content.objective || '',
      displayOrder: all.length + 1,
      linkOnly: false,
      assessments: [],
      topics,
    }]);
    setOpenModuleIds([nextIndex]);
    setOpenTopicKeys([`${nextIndex}:0`]);
    setOpenTocModules((current) => current.includes(nextIndex) ? current : [...current, nextIndex]);
    selectModule(nextIndex, true);
    setAiPanel(null);
  };

  const applyEntireCourse = (plan: GeneratedCoursePlan) => {
    setTitle(plan.title || title);
    setSubtitle(plan.subtitle || '');
    setSummary(plan.summary || '');
    setOutcomes(plan.learningOutcomes || '');
    setPrerequisites(plan.prerequisites || '');
    if (plan.difficulty === 'Beginner' || plan.difficulty === 'Intermediate' || plan.difficulty === 'Advanced') {
      setDifficulty(plan.difficulty);
    }
    if (plan.estimatedDurationHours > 0) setHours(plan.estimatedDurationHours);
    if (plan.tags?.length) setTags(plan.tags.join(', '));
    const nextModules: KnowledgeModule[] = (plan.modules || []).map((module, moduleIndex) => {
      const topics = (module.topics || []).map((topic, topicIndex) => ({
        title: stripLeadingSequence(topic.title || '') || `Topic ${topicIndex + 1}`,
        slug: '',
        objective: topic.objective || '',
        estimatedMinutes: topic.estimatedMinutes || 12,
        displayOrder: topic.displayOrder || topicIndex + 1,
        blocks: (topic.blocks || []).map((block, blockIndex) => ({
          type: block.type || 'Paragraph',
          content: block.content || '',
          language: block.language || undefined,
          altText: block.altText || undefined,
          caption: block.caption || undefined,
          url: block.url || undefined,
          metadataJson: block.metadataJson || undefined,
          displayOrder: blockIndex + 1,
        })),
        relatedLabs: (topic.relatedLabs || []).map((lab) => ({
          id: lab.id,
          name: lab.name,
          slug: '',
          difficulty: lab.difficulty || plan.difficulty,
          duration: lab.duration,
        })),
        references: [],
      }));
      const lastTopicOrder = topics.reduce((max, topic) => Math.max(max, topic.displayOrder || 0), 0);
      const assessmentStart = Math.max(lastTopicOrder, topics.length) + 1;
      return {
        title: stripLeadingSequence(module.title || '') || `Module ${moduleIndex + 1}`,
        goal: module.goal || '',
        displayOrder: module.displayOrder || moduleIndex + 1,
        linkOnly: false,
        topics,
        assessments: (module.assessments || []).map((assessment, assessmentIndex) => ({
          title: assessment.title || 'Assessment',
          description: assessment.description || '',
          passingScore: assessment.passingScore || 70,
          displayOrder: assessmentStart + assessmentIndex,
          questions: (assessment.questions || []).map((question, questionIndex) => ({
            prompt: question.prompt,
            explanation: question.explanation || '',
            displayOrder: questionIndex + 1,
            options: (question.options || []).map((option, optionIndex) => ({
              text: option.text,
              isCorrect: option.isCorrect,
              displayOrder: optionIndex + 1,
            })),
          })),
        })),
      };
    });
    setModules(nextModules.length ? nextModules : [emptyModule(1)]);
    setOpenModuleIds(nextModules.map((_, index) => index));
    setOpenTopicKeys(nextModules.flatMap((module, moduleIndex) => module.topics.map((_, topicIndex) => `${moduleIndex}:${topicIndex}`)));
    setOpenTocModules(nextModules.map((_, index) => index));
    setSelection({ kind: 'details' });
    setAiPanel(null);
    requestAnimationFrame(() => focusPageTop('course-editor-top'));
  };
  applyCourseRef.current = applyEntireCourse;

  useEffect(() => {
    if (!courseJobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await contentApi.generationJob(courseJobId);
        const job = response.data.job;
        if (cancelled || !job) return;
        setCourseStage(job.stage || job.status);
        if (job.status === 'Completed') {
          if (applyingCourse.current) return;
          applyingCourse.current = true;
          const planResponse = await contentApi.coursePlan(courseJobId);
          if (cancelled) return;
          if (!planResponse.data.success || !planResponse.data.coursePlan) {
            applyingCourse.current = false;
            setError(planResponse.data.message || 'The course draft was empty.');
            setCourseJobId(null);
            setCourseStage(null);
            return;
          }
          applyCourseRef.current(planResponse.data.coursePlan);
          setMessage('AI drafted the entire course. Module labs are saved. Review, then save the draft.');
          setCourseJobId(null);
          setCourseStage(null);
          return;
        }
        if (job.status === 'Failed') {
          setError(job.errorMessage || 'Course generation failed.');
          setCourseJobId(null);
          setCourseStage(null);
        }
      } catch (err: unknown) {
        applyingCourse.current = false;
        if (!cancelled) setError(err instanceof Error ? err.message : 'Course generation failed.');
      }
    };
    void poll();
    const timer = window.setInterval(() => void poll(), 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [courseJobId]);

  const openEntireCourse = async () => {
    const hasContent = Boolean(title.trim()) || modules.some((module) => module.title.trim() || module.topics.some((topic) => topic.title.trim()));
    if (hasContent) {
      const ok = await askConfirm({
        title: 'Replace this course with an AI draft?',
        description: 'Course details and modules in the editor will be replaced. Labs for the new course are saved as drafts.',
        confirmLabel: 'Replace',
        variant: 'danger',
      });
      if (!ok) return;
    }
    setAiTopic(title);
    setAiPanel('course');
    setContextMenu(null);
  };

  const generate = async (generationType: string, action = 'generate') => {
    setGenerating(true);
    setError(null);
    const currentTopic = selection.kind === 'topic' ? modules[selection.moduleIndex]?.topics[selection.topicIndex] : undefined;
    try {
      if (generationType === 'entire-course') {
        const focus = (aiTopic || title).trim();
        const notes = aiPrompt.trim();
        const prompt = [
          focus ? `Create an entire course: ${focus}` : 'Create an entire course.',
          `Difficulty: ${difficulty || 'Intermediate'}`,
          notes ? `Additional author notes: ${notes}` : 'Cover the subject in depth with practical scenarios.',
        ].join('\n');
        const created = await contentApi.createGeneration(
          { prompt, contentType: 'Course', schemaVersion: 'v1' },
          crypto.randomUUID(),
        );
        if (!created.data.jobId) {
          throw new Error(created.data.message || 'Course generation was not queued.');
        }
        setCourseJobId(created.data.jobId);
        setCourseStage('Outline');
        setAiPanel(null);
        setMessage('Course generation started. This page fills in the course when it finishes.');
        return;
      }
      const existing = currentTopic
        ? JSON.stringify({ title: currentTopic.title, objective: currentTopic.objective, blocks: currentTopic.blocks })
        : undefined;
      const response = await contentApi.generate({
        topic: aiTopic || currentTopic?.title || title,
        prompt: aiPrompt,
        action,
        difficulty,
        generationType,
        existingContentJson: action === 'revise' ? existing : undefined,
        targetCourseId: courseId,
      });
      if (generationType === 'assessment' && selection.kind !== 'details') {
        const questions = (response.data.content.questions || []).map((q, i) => ({
          prompt: q.prompt,
          explanation: q.explanation,
          displayOrder: i + 1,
          options: (q.options || []).map((o, n) => ({ text: o.text, isCorrect: o.isCorrect, displayOrder: n + 1 })),
        }));
        const mi = selection.kind === 'details' ? 0 : selection.moduleIndex;
        setModules((all) => all.map((module, i) => i !== mi ? module : applyFlowOrders({
          ...module,
          linkOnly: false,
          assessments: [...(module.assessments || []), {
            title: stripLeadingSequence(response.data.content.title || '') || 'Assessment',
            description: response.data.content.objective || '',
            passingScore: 70,
            displayOrder: nextFlowOrder(module.topics, module.assessments),
            questions: questions.length ? questions : emptyAssessment(1).questions,
          }],
        })));
        setSelection({ kind: 'assessment', moduleIndex: mi, assessmentIndex: (modules[mi]?.assessments || []).length });
        setMessage('AI assessment added. Edit before submitting.');
      } else if (generationType === 'topic' || action === 'revise') {
        if (action === 'revise' && selection.kind === 'topic') {
          const first = response.data.content.sections[0];
          const refs = (response.data.content.references || []).map((url, n) => ({ title: url, url, displayOrder: n + 1 }));
          setModules((all) => all.map((module, i) => i !== selection.moduleIndex ? module : {
            ...module,
            linkOnly: false,
            topics: module.topics.map((topic, j) => j !== selection.topicIndex ? topic : {
              ...topic,
              title: stripLeadingSequence(response.data.content.title || topic.title) || topic.title,
              objective: response.data.content.objective || topic.objective,
              references: refs.length ? refs : topic.references,
              blocks: (first?.blocks || topic.blocks).map((block, n) => ({
                type: 'type' in block ? block.type : 'paragraph',
                content: 'content' in block ? block.content : '',
                language: 'language' in block ? block.language : undefined,
                displayOrder: n + 1,
              })),
            }),
          }));
          setMessage('AI updated this topic. Review before saving.');
        } else {
          applyGenerated(response.data.content, true);
          setMessage('AI topic added. Edit before submitting.');
        }
      } else {
        applyGenerated(response.data.content);
        setMessage('AI module added. Edit before submitting.');
      }
      setAiPanel(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const allModuleIds = modules.map((_, i) => i);
  const allTopicKeys = modules.flatMap((m, mi) => m.topics.map((_, ti) => `${mi}:${ti}`));
  const allExpanded =
    allModuleIds.length > 0
    && allModuleIds.every((id) => openModuleIds.includes(id))
    && allTopicKeys.every((key) => openTopicKeys.includes(key));
  const toggleAll = () => {
    if (allExpanded) {
      setOpenModuleIds([]);
      setOpenTopicKeys([]);
    } else {
      setOpenModuleIds(allModuleIds);
      setOpenTopicKeys(allTopicKeys);
    }
  };

  const addLabToTopic = (moduleIndex: number, topicIndex: number, lab: RelatedLab) => {
    setModules((all) => all.map((module, i) => i !== moduleIndex ? module : {
      ...module,
      topics: module.topics.map((topic, j) => {
        if (j !== topicIndex || (topic.relatedLabs || []).some((item) => item.id === lab.id)) return topic;
        return { ...topic, relatedLabs: [...(topic.relatedLabs || []), lab] };
      }),
    }));
  };

  const removeLabFromTopic = (moduleIndex: number, topicIndex: number, labId: number) => {
    setModules((all) => all.map((module, i) => i !== moduleIndex ? module : {
      ...module,
      topics: module.topics.map((topic, j) => j !== topicIndex
        ? topic
        : { ...topic, relatedLabs: (topic.relatedLabs || []).filter((lab) => lab.id !== labId) }),
    }));
  };

  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-context-menu]')) return;
      setContextMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [contextMenu]);

  const addTopicToModule = (moduleIndex: number) => {
    const module = modules[moduleIndex];
    const order = nextFlowOrder(module?.topics, module?.assessments);
    const nextIndex = module?.topics.length || 0;
    setModules((all) => all.map((item, i) => i !== moduleIndex ? item : applyFlowOrders({
      ...item,
      linkOnly: false,
      topics: [...item.topics, emptyTopic(order)],
    })));
    selectTopic(moduleIndex, nextIndex);
    setContextMenu(null);
    requestAnimationFrame(() => focusPageTop('course-editor-top'));
  };

  const addAssessmentToModule = (moduleIndex: number) => {
    const module = modules[moduleIndex];
    const order = nextFlowOrder(module?.topics, module?.assessments);
    const nextIndex = module?.assessments?.length || 0;
    setModules((all) => all.map((item, i) => i !== moduleIndex ? item : applyFlowOrders({
      ...item,
      linkOnly: false,
      assessments: [...(item.assessments || []), emptyAssessment(order)],
    })));
    setOpenTocModules((current) => current.includes(moduleIndex) ? current : [...current, moduleIndex]);
    setSelection({ kind: 'assessment', moduleIndex, assessmentIndex: nextIndex });
    setContextMenu(null);
  };

  const askAiFor = (moduleIndex: number, panel: 'topic' | 'assessment', focus?: string) => {
    setSelection({ kind: 'module', moduleIndex });
    setAiTopic(focus || modules[moduleIndex]?.title || title);
    setAiPanel(panel);
    setContextMenu(null);
  };

  const removeModuleCascade = async (moduleIndex: number) => {
    const module = modules[moduleIndex];
    const name = module?.title || 'this module';
    const first = await askConfirm({
      title: `Remove ${name}?`,
      description: 'This deletes the module and every topic, assessment, and lab that belongs only to it.',
      confirmLabel: 'Continue',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!first) return;
    const second = await askConfirm({
      title: 'Delete everything in this module?',
      description: 'This cannot be undone. Topics, assessments, and labs used only here will be deleted.',
      confirmLabel: 'Delete module',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!second) return;
    if (module?.id) {
      try {
        await contentApi.deleteModule(module.id);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Unable to remove that module.');
        return;
      }
    }
    setModules((all) => all.filter((_, i) => i !== moduleIndex));
    setSelection({ kind: 'details' });
    setContextMenu(null);
  };

  const removeTopicAt = async (moduleIndex: number, topicIndex: number) => {
    const topic = modules[moduleIndex]?.topics[topicIndex];
    const ok = await askConfirm({
      title: `Remove ${topic?.title || 'this topic'}?`,
      description: 'The topic is removed from this module. Save the course to keep the change.',
      confirmLabel: 'Remove topic',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    setModules((all) => all.map((item, i) => i !== moduleIndex ? item : applyFlowOrders({
      ...item,
      topics: item.topics.filter((_, j) => j !== topicIndex),
    })));
    setSelection({ kind: 'module', moduleIndex });
    setContextMenu(null);
  };

  const openCreateLab = (moduleIndex: number, topicIndex: number) => {
    const topic = modules[moduleIndex]?.topics[topicIndex];
    if (!courseId || !topic?.id) {
      setError('Save the course first, then create a lab for this topic.');
      setContextMenu(null);
      return;
    }
    setContextMenu(null);
    navigate(`/admin/labs/new?courseId=${courseId}&topicId=${topic.id}`);
  };

  const openAddExistingLab = (moduleIndex: number, topicIndex: number) => {
    selectTopic(moduleIndex, topicIndex);
    setBrowse('lab');
    setContextMenu(null);
  };

  const selectModule = (moduleIndex: number, focus = false) => {
    setOpenTocModules((current) => current.includes(moduleIndex) ? current : [...current, moduleIndex]);
    setSelection({ kind: 'module', moduleIndex });
    if (focus) setPendingModuleFocus(moduleIndex);
  };

  useLayoutEffect(() => {
    if (pendingModuleFocus == null) return;
    if (selection.kind !== 'module' || selection.moduleIndex !== pendingModuleFocus) return;
    focusPageTop('course-editor-top');
    setPendingModuleFocus(null);
  }, [pendingModuleFocus, selection]);

  const selectTopic = (moduleIndex: number, topicIndex: number) => {
    setOpenTocModules((current) => current.includes(moduleIndex) ? current : [...current, moduleIndex]);
    setSelection({ kind: 'topic', moduleIndex, topicIndex });
  };

  const moveModule = (from: number, to: number) => {
    setModules((all) => arrayMove(all, from, to));
    setSelection((current) => {
      if (current.kind === 'details') return current;
      const mapIndex = (index: number) => {
        if (index === from) return to;
        if (from < to && index > from && index <= to) return index - 1;
        if (from > to && index >= to && index < from) return index + 1;
        return index;
      };
      if (current.kind === 'module') return { kind: 'module', moduleIndex: mapIndex(current.moduleIndex) };
      if (current.kind === 'assessment') {
        return { kind: 'assessment', moduleIndex: mapIndex(current.moduleIndex), assessmentIndex: current.assessmentIndex };
      }
      return { kind: 'topic', moduleIndex: mapIndex(current.moduleIndex), topicIndex: current.topicIndex };
    });
  };

  const moveTopic = (fromMi: number, fromTi: number, toMi: number, toTi: number) => {
    setModules((all) => {
      const source = all[fromMi]?.topics[fromTi];
      if (!source) return all;
      const without = all.map((module, i) => i === fromMi
        ? { ...module, topics: module.topics.filter((_, j) => j !== fromTi) }
        : module);
      const insertAt = fromMi === toMi && fromTi < toTi ? toTi - 1 : toTi;
      return without.map((module, i) => {
        if (i !== toMi) return module;
        const topics = [...module.topics];
        const clamped = Math.max(0, Math.min(insertAt, topics.length));
        topics.splice(clamped, 0, source);
        return { ...module, topics };
      });
    });
    setOpenTocModules((current) => current.includes(toMi) ? current : [...current, toMi]);
    setSelection({ kind: 'topic', moduleIndex: toMi, topicIndex: fromMi === toMi && fromTi < toTi ? toTi - 1 : toTi });
  };

  const onDragStart = (event: React.DragEvent, payload: string) => {
    event.dataTransfer.setData('text/plain', payload);
    event.dataTransfer.effectAllowed = 'move';
    setDragging(payload);
  };

  const onDropModule = (event: React.DragEvent, targetIndex: number) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = parseDrag(event.dataTransfer.getData('text/plain'));
    setDragging(null);
    if (!payload) return;
    if (payload.type === 'module') {
      moveModule(payload.index, targetIndex);
      return;
    }
    moveTopic(payload.moduleIndex, payload.topicIndex, targetIndex, modules[targetIndex]?.topics.length || 0);
  };

  const onDropFlow = (event: React.DragEvent, targetMi: number, targetFi: number) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = parseDrag(event.dataTransfer.getData('text/plain'));
    setDragging(null);
    if (!payload || payload.type !== 'flow' || payload.moduleIndex !== targetMi) return;
    setModules((all) => all.map((module, i) => {
      if (i !== targetMi) return module;
      return applyFlowOrders(module, arrayMove(moduleFlow(module.topics, module.assessments), payload.flowIndex, targetFi));
    }));
  };

  const onDropTopic = (event: React.DragEvent, targetMi: number, targetTi: number) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = parseDrag(event.dataTransfer.getData('text/plain'));
    setDragging(null);
    if (!payload || payload.type !== 'topic') return;
    moveTopic(payload.moduleIndex, payload.topicIndex, targetMi, targetTi);
  };

  const selectedModule = selection.kind === 'details' ? undefined : modules[selection.moduleIndex];
  const selectedTopic = selection.kind === 'topic' ? selectedModule?.topics[selection.topicIndex] : undefined;
  const selectedAssessment = selection.kind === 'assessment' ? selectedModule?.assessments?.[selection.assessmentIndex] : undefined;
  const labsOnThisCourse = useMemo(() => {
    const byId = new Map<number, RelatedLab>();
    availableLabs.forEach((lab) => {
      byId.set(lab.id, { id: lab.id, name: lab.name, slug: lab.slug, duration: lab.duration, difficulty: lab.difficulty });
    });
    modules.forEach((module) => {
      module.topics.forEach((topic) => {
        (topic.relatedLabs || []).forEach((lab) => {
          if (!byId.has(lab.id)) byId.set(lab.id, lab);
        });
      });
    });
    return [...byId.values()];
  }, [availableLabs, modules]);

  if (loading) return <PageLoading label="Loading course…" />;

  return (
    <div id="course-editor-top" tabIndex={-1} className="relative max-w-7xl mx-auto px-4 py-10 outline-none">
      {generating && <LoadingOverlay />}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <Link to="/admin/dashboard?tab=courses" className="inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to courses
          </Link>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">{courseId ? 'Edit course' : 'New course'}</h1>
          {workflowStatus && <p className="mt-1 text-sm text-gray-500">Status: {workflowStatus}</p>}
          <p className="mt-1 text-sm text-gray-500">You can save and submit for review at any status. Submitting a live course sends the changes back to review.</p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex gap-2">
          {courseSlug ? (
            <Button variant="outline" type="button" onClick={() => openLearnerTab(`/courses/${courseSlug}`)}>View</Button>
          ) : null}
          <Button variant="outline" type="button" onClick={() => setPreview(!preview)}>{preview ? 'Edit' : 'Preview'}</Button>
          <Button variant="secondary" type="button" onClick={() => setConfirmAction('draft')} disabled={saving}>Save draft</Button>
          <Button type="button" onClick={() => setConfirmAction('review')} disabled={saving}>Submit for review</Button>
          </div>
          <ActionStatus className="w-full max-w-md" message={message} error={error} />
        </div>
      </div>
      {courseStage && (
        <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-950">
          Generating the course. Stage: {courseStage}
        </div>
      )}
      {reviewNotes && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-semibold">Review suggestions</p>
          <p className="mt-1 whitespace-pre-wrap">{reviewNotes}</p>
          <p className="mt-2 text-amber-800">Apply the feedback, save, then submit again for review.</p>
        </div>
      )}

      {preview ? (
        <div className="space-y-8">
          <h2 className="text-4xl font-bold">{title}</h2>
          <p className="text-xl text-gray-600">{subtitle}</p>
          <CurriculumPreview
            modules={modules}
            openModuleIds={openModuleIds}
            openTopicKeys={openTopicKeys}
            onToggleModule={(index) => setOpenModuleIds((current) => toggleId(current, index))}
            onToggleTopic={(key) => setOpenTopicKeys((current) => toggleExclusiveKey(current, key))}
            onToggleAll={toggleAll}
            allExpanded={allExpanded}
          />
        </div>
      ) : (
        <div className="grid lg:grid-cols-[18rem_minmax(0,1fr)] gap-6 items-start">
          <nav aria-label="Course editor contents" className="lg:sticky lg:top-24 rounded-2xl border border-gray-200 bg-white p-3">
            <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Contents</p>
            <button
              type="button"
              className={`w-full text-left rounded-lg px-3 py-2 text-sm ${
                selection.kind === 'details' ? 'bg-primary-50 text-primary-800 font-semibold' : 'text-gray-800 hover:bg-gray-50'
              }`}
              onClick={() => setSelection({ kind: 'details' })}
            >
              Course details
            </button>
            <ul className="mt-2 space-y-2">
              {modules.map((module, mi) => {
                const open = openTocModules.includes(mi);
                const stripe = moduleStripeClass(mi);
                const moduleSelected = selection.kind === 'module' && selection.moduleIndex === mi;
                return (
                  <li
                    key={`toc-mod-${mi}`}
                    className={`${stripe.box} ${dragging === `module:${mi}` ? 'opacity-50' : ''}`}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => onDropModule(event, mi)}
                  >
                    <div className={`flex items-start gap-1 px-2 py-1.5 ${moduleSelected ? 'bg-white/80' : stripe.hover}`}>
                      <span
                        className="mt-1 text-gray-400 cursor-grab shrink-0"
                        draggable
                        onDragStart={(event) => onDragStart(event, `module:${mi}`)}
                        onDragEnd={() => setDragging(null)}
                        aria-label="Drag to reorder module"
                      >
                        <GripVertical className="h-4 w-4" />
                      </span>
                      <button
                        type="button"
                        className="mt-1 shrink-0 text-gray-500"
                        aria-expanded={open}
                        onClick={() => setOpenTocModules((current) => toggleId(current, mi))}
                      >
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      <button
                        type="button"
                        className={`flex-1 text-left text-sm font-semibold ${stripe.title}`}
                        onClick={() => selectModule(mi)}
                      >
                        {String(mi + 1).padStart(2, '0')} {module.title || 'Untitled module'}
                      </button>
                      <button
                        type="button"
                        className="mt-1 shrink-0 text-gray-500"
                        aria-label="Module actions"
                        data-context-menu
                        onClick={() => setContextMenu(contextMenu?.kind === 'module' && contextMenu.moduleIndex === mi ? null : { kind: 'module', moduleIndex: mi })}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    {contextMenu?.kind === 'module' && contextMenu.moduleIndex === mi && (
                      <div data-context-menu className="relative z-20 mx-2 mb-1 rounded-lg border border-gray-200 bg-white shadow-lg p-1">
                        <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50" onClick={() => addTopicToModule(mi)}>Add a topic</button>
                        <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50" onClick={() => askAiFor(mi, 'topic', module.title)}>Ask AI for a topic</button>
                        <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50" onClick={() => addAssessmentToModule(mi)}>Add an assessment</button>
                        <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50" onClick={() => askAiFor(mi, 'assessment', module.title)}>Ask AI for assessment</button>
                        <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded text-error-700 hover:bg-error-50" onClick={() => removeModuleCascade(mi)}>Remove module</button>
                      </div>
                    )}
                    {open && (
                      <ul className="px-2 pb-2 space-y-1">
                        {moduleFlow(module.topics, module.assessments).map((item, fi) => {
                          const topicStripe = topicStripeClass(mi, fi);
                          if (item.kind === 'assessment') {
                            const ai = item.assessmentIndex;
                            const selected = selection.kind === 'assessment' && selection.moduleIndex === mi && selection.assessmentIndex === ai;
                            return (
                              <li
                                key={`toc-as-${mi}-${ai}`}
                                draggable
                                onDragStart={(event) => {
                                  event.stopPropagation();
                                  onDragStart(event, `flow:${mi}:${fi}`);
                                }}
                                onDragEnd={() => setDragging(null)}
                                onDragOver={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                }}
                                onDrop={(event) => onDropFlow(event, mi, fi)}
                                className={dragging === `flow:${mi}:${fi}` ? 'opacity-50' : ''}
                              >
                                <button
                                  type="button"
                                  className={`w-full text-left rounded-lg px-2 py-1.5 text-sm flex items-start gap-1 ${
                                    selected ? 'bg-white font-semibold shadow-sm text-gray-900' : `${topicStripe.title} ${topicStripe.hover}`
                                  }`}
                                  onClick={() => {
                                    setOpenTocModules((current) => current.includes(mi) ? current : [...current, mi]);
                                    setSelection({ kind: 'assessment', moduleIndex: mi, assessmentIndex: ai });
                                  }}
                                >
                                  <GripVertical className="h-3.5 w-3.5 mt-0.5 text-gray-400 shrink-0 cursor-grab" />
                                  <ClipboardList className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                  <span>Assessment: {item.assessment.title || 'Untitled assessment'}</span>
                                </button>
                              </li>
                            );
                          }
                          const ti = item.topicIndex;
                          const topic = item.topic;
                          const topicSelected = selection.kind === 'topic' && selection.moduleIndex === mi && selection.topicIndex === ti;
                          const menuOpen = contextMenu?.kind === 'topic' && contextMenu.moduleIndex === mi && contextMenu.topicIndex === ti;
                          return (
                            <li
                              key={`toc-top-${mi}-${ti}`}
                              draggable
                              onDragStart={(event) => {
                                event.stopPropagation();
                                onDragStart(event, `flow:${mi}:${fi}`);
                              }}
                              onDragEnd={() => setDragging(null)}
                              onDragOver={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                              onDrop={(event) => onDropFlow(event, mi, fi)}
                              className={dragging === `flow:${mi}:${fi}` ? 'opacity-50' : ''}
                            >
                              <div className={`flex items-start gap-1 rounded-lg ${topicSelected ? 'bg-white font-semibold shadow-sm text-gray-900' : ''}`}>
                                <button
                                  type="button"
                                  className={`flex-1 text-left px-2 py-1.5 text-sm flex items-start gap-1 ${
                                    topicSelected ? '' : `${topicStripe.title} ${topicStripe.hover}`
                                  }`}
                                  onClick={() => selectTopic(mi, ti)}
                                >
                                  <GripVertical className="h-3.5 w-3.5 mt-0.5 text-gray-400 shrink-0 cursor-grab" />
                                  <span>{topic.title || 'Untitled topic'}</span>
                                </button>
                                <button
                                  type="button"
                                  className="mt-1 mr-1 shrink-0 text-gray-500"
                                  aria-label="Topic actions"
                                  data-context-menu
                                  onClick={() => setContextMenu(menuOpen ? null : { kind: 'topic', moduleIndex: mi, topicIndex: ti })}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </button>
                              </div>
                              {menuOpen && (
                                <div data-context-menu className="rounded-lg border border-gray-200 bg-white shadow-lg p-1">
                                  <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50" onClick={() => { setAiTopic(topic.title); setAiPanel('review'); selectTopic(mi, ti); setContextMenu(null); }}>Review and update with AI</button>
                                  <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50" onClick={() => openCreateLab(mi, ti)}>Create lab</button>
                                  <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50" onClick={() => openAddExistingLab(mi, ti)}>Add existing lab</button>
                                  <button type="button" className="w-full text-left px-2 py-1.5 text-sm rounded text-error-700 hover:bg-error-50" onClick={() => removeTopicAt(mi, ti)}>Remove topic</button>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 relative">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                data-context-menu
                onClick={() => setContextMenu(contextMenu?.kind === 'module-add' ? null : { kind: 'module-add' })}
              >
                <Plus className="h-4 w-4 mr-1" /> Add module
              </Button>
              {contextMenu?.kind === 'module-add' && (
                <div data-context-menu className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg p-1">
                  <button type="button" className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-50" onClick={() => {
                    const nextIndex = modules.length;
                    setModules([...modules, emptyModule(modules.length + 1)]);
                    selectModule(nextIndex, true);
                    setContextMenu(null);
                  }}>Add a new module</button>
                  <button type="button" className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-50" onClick={() => {
                    setAiTopic(title);
                    setAiPanel('module');
                    setContextMenu(null);
                  }}>Ask AI to add a new module</button>
                  <button type="button" className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-50" onClick={() => { void openEntireCourse(); }}>Ask AI to create the entire course</button>
                  <button type="button" className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-50" onClick={() => {
                    setBrowse('module');
                    setContextMenu(null);
                  }}>Browse existing modules</button>
                </div>
              )}
            </div>
          </nav>

          <div className="min-w-0 space-y-6">
            {selection.kind === 'details' && (
              <div className="space-y-6 rounded-2xl border border-gray-200 bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">Course details</h2>
                  <Button type="button" variant="outline" size="sm" onClick={() => { void openEntireCourse(); }}>
                    <Sparkles className="h-4 w-4 mr-1" /> Create entire course
                  </Button>
                </div>
                <Input id="title" label="Course title" value={title} onChange={(e) => setTitle(e.target.value)} />
                <Input id="subtitle" label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
                <label className="block text-sm font-medium text-gray-700">Summary
                  <textarea className="mt-1 w-full rounded-md border-gray-300" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
                </label>
                <label className="block text-sm font-medium text-gray-700">Learning outcomes (one per line)
                  <textarea className="mt-1 w-full rounded-md border-gray-300" rows={5} value={outcomes} onChange={(e) => setOutcomes(e.target.value)} />
                </label>
                <label className="block text-sm font-medium text-gray-700">Prerequisites
                  <textarea className="mt-1 w-full rounded-md border-gray-300" rows={3} value={prerequisites} onChange={(e) => setPrerequisites(e.target.value)} />
                </label>
                <div className="grid md:grid-cols-3 gap-4">
                  <label className="block text-sm font-medium text-gray-700">Category
                    <select className="mt-1 w-full rounded-md border-gray-300" value={categoryId} onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}>
                      <option value="">Select</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </label>
                  <label className="block text-sm font-medium text-gray-700">Difficulty
                    <select className="mt-1 w-full rounded-md border-gray-300" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                      <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
                    </select>
                  </label>
                  <Input id="hours" type="number" label="Duration (hours)" value={String(hours)} onChange={(e) => setHours(Number(e.target.value))} />
                  <Input id="order" type="number" label="Menu sequence" value={String(displayOrder)} onChange={(e) => setDisplayOrder(Number(e.target.value))} />
                  <Input id="price" type="number" label="Price (INR)" value={String(price)} onChange={(e) => setPrice(Number(e.target.value))} />
                  <Input id="discount" type="number" label="Discount %" value={String(discountPercent)} onChange={(e) => setDiscountPercent(Number(e.target.value))} />
                </div>
                <Input id="tags" label="Tags" value={tags} onChange={(e) => setTags(e.target.value)} />
                <ContentOwnerSelect value={authorId} currentLabel={authorLabel} onChange={setAuthorId} />
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} />
                  Featured on the catalog
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                  Public — visible to everyone after approval. Uncheck to keep it private for invited learners.
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={unlimitedSeats}
                    onChange={(e) => setUnlimitedSeats(e.target.checked)}
                  />
                  Unlimited seats (disable enrollment capacity limit)
                </label>
                {!unlimitedSeats && (
                  <Input
                    id="max-seats"
                    type="number"
                    label="Max seats"
                    value={String(maxSeats)}
                    onChange={(e) => setMaxSeats(Math.max(1, Number(e.target.value) || 1))}
                  />
                )}
                {courseId && changelog.length > 0 && (
                  <div className="rounded-lg border border-gray-200 p-3 text-sm">
                    <p className="font-medium text-gray-900">Change log</p>
                    <ul className="mt-2 space-y-1 text-gray-600">
                      {changelog.slice(0, 8).map((entry) => (
                        <li key={`${entry.dateCreated}-${entry.action}`}>
                          {entry.action} · {entry.changedBy || 'unknown'}{entry.changedByRole ? ` (${entry.changedByRole})` : ''} · {new Date(entry.dateCreated).toLocaleString()}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <label className="block text-sm font-medium text-gray-700">Thumbnail
                  {coverImageUrl && <img src={coverImageUrl} alt="" className="mt-2 h-32 w-full max-w-sm object-cover rounded-lg" />}
                  <input
                    type="file"
                    accept="image/*"
                    className="mt-1 block text-sm"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const response = await contentApi.uploadMedia(file, title || file.name, undefined, {
                          courseId,
                          catalogKind: 'courses',
                          entityName: title,
                          topicName: 'cover',
                        });
                        setCoverImageId(response.data.media.id);
                        setCoverImageUrl(response.data.media.url);
                      } catch {
                        setError('Thumbnail upload failed.');
                      }
                    }}
                  />
                </label>
                <div>
                  <p className="text-sm font-medium text-gray-700">Linked projects (optional)</p>
                  <p className="text-xs text-gray-500 mb-2">Project completion is not required to finish the course.</p>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 p-2 space-y-1">
                    {availableProjects.map((project) => (
                      <label key={project.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={projectIds.includes(project.id)}
                          onChange={(e) => setProjectIds((current) => e.target.checked ? [...current, project.id] : current.filter((id) => id !== project.id))}
                        />
                        {project.title}
                      </label>
                    ))}
                    {availableProjects.length === 0 && <p className="text-xs text-gray-500">No projects available yet.</p>}
                  </div>
                </div>
              </div>
            )}

            {selection.kind === 'module' && selectedModule && (
              <div id="course-module-editor" className="space-y-4 rounded-2xl border border-gray-200 bg-gray-100 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h2 className="text-lg font-semibold text-gray-900">Module {selection.moduleIndex + 1}</h2>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => addTopicToModule(selection.moduleIndex)}>Add a topic</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => askAiFor(selection.moduleIndex, 'topic', selectedModule.title)}>
                      <Sparkles className="h-4 w-4 mr-1" /> Ask AI for a topic
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => addAssessmentToModule(selection.moduleIndex)}>Add an assessment</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => askAiFor(selection.moduleIndex, 'assessment', selectedModule.title)}>
                      <Sparkles className="h-4 w-4 mr-1" /> Ask AI for assessment
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeModuleCascade(selection.moduleIndex)}>
                      Remove from course
                    </Button>
                  </div>
                </div>
                <Input
                  id={`mod-${selection.moduleIndex}`}
                  label="Module name"
                  value={selectedModule.title}
                  onChange={(e) => setModules((all) => all.map((m, i) => i === selection.moduleIndex ? { ...m, title: e.target.value } : m))}
                />
                <label className="block text-sm font-medium text-gray-700">Goal
                  <textarea
                    className="mt-1 w-full rounded-md border-gray-300"
                    rows={3}
                    value={selectedModule.goal || ''}
                    onChange={(e) => setModules((all) => all.map((m, i) => i === selection.moduleIndex ? { ...m, goal: e.target.value } : m))}
                  />
                </label>
                {selectedModule.courseCount && selectedModule.courseCount > 1 ? (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    This module is attached to {selectedModule.courseCount} courses. Renaming or editing topics updates it everywhere it is used.
                  </p>
                ) : null}
                {selectedModule.author?.displayName ? (
                  <div className="max-w-xl"><AuthorCard author={selectedModule.author} compact /></div>
                ) : null}
                <p className="text-sm text-gray-600">
                  {selectedModule.topics.length} topics · {(selectedModule.assessments || []).length} assessments.
                  Drag topics and assessments in the contents list to set the order learners see.
                </p>
              </div>
            )}

            {selection.kind === 'assessment' && selectedModule && selectedAssessment && (
              <AssessmentFlowEditor
                assessment={selectedAssessment}
                onAskAi={() => askAiFor(selection.moduleIndex, 'assessment', selectedAssessment.title || selectedModule.title)}
                onRemove={() => {
                  const mi = selection.moduleIndex;
                  const ai = selection.assessmentIndex;
                  setModules((all) => all.map((m, i) => i !== mi ? m : applyFlowOrders({
                    ...m,
                    assessments: (m.assessments || []).filter((_, n) => n !== ai),
                  })));
                  setSelection({ kind: 'module', moduleIndex: mi });
                }}
                onChange={(next) => setModules((all) => all.map((m, i) => i !== selection.moduleIndex ? m : {
                  ...m,
                  linkOnly: false,
                  assessments: (m.assessments || []).map((item, n) => n === selection.assessmentIndex ? next : item),
                }))}
              />
            )}

            {selection.kind === 'topic' && selectedModule && selectedTopic && (
              <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      Module {selection.moduleIndex + 1} · Topic {selection.topicIndex + 1}
                    </p>
                    <h2 className="text-lg font-semibold text-gray-900">{selectedTopic.title || 'Untitled topic'}</h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => openCreateLab(selection.moduleIndex, selection.topicIndex)}>
                      <FlaskConical className="h-4 w-4 mr-1" /> Create lab
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => openAddExistingLab(selection.moduleIndex, selection.topicIndex)}>
                      Add existing lab
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => { setAiTopic(selectedTopic.title); setAiPanel('review'); }}>
                      <Sparkles className="h-4 w-4 mr-1" /> Review and update with AI
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeTopicAt(selection.moduleIndex, selection.topicIndex)}>
                      Remove topic
                    </Button>
                  </div>
                </div>
                <Input
                  id={`top-${selection.moduleIndex}-${selection.topicIndex}`}
                  label="Topic title"
                  value={selectedTopic.title}
                  onChange={(e) => setModules((all) => all.map((m, i) => i === selection.moduleIndex
                    ? { ...m, topics: m.topics.map((t, j) => j === selection.topicIndex ? { ...t, title: e.target.value } : t) }
                    : m))}
                />
                <Input
                  id={`slug-${selection.moduleIndex}-${selection.topicIndex}`}
                  label="Topic slug"
                  value={selectedTopic.slug || ''}
                  onChange={(e) => setModules((all) => all.map((m, i) => i === selection.moduleIndex
                    ? { ...m, topics: m.topics.map((t, j) => j === selection.topicIndex ? { ...t, slug: e.target.value } : t) }
                    : m))}
                />
                <label className="block text-sm font-medium text-gray-700">Objective
                  <textarea
                    className="mt-1 w-full rounded-md border-gray-300"
                    rows={2}
                    value={selectedTopic.objective || ''}
                    onChange={(e) => setModules((all) => all.map((m, i) => i === selection.moduleIndex
                      ? { ...m, topics: m.topics.map((t, j) => j === selection.topicIndex ? { ...t, objective: e.target.value } : t) }
                      : m))}
                  />
                </label>
                <Input
                  id={`mins-${selection.moduleIndex}-${selection.topicIndex}`}
                  type="number"
                  label="Minutes"
                  value={String(selectedTopic.estimatedMinutes)}
                  onChange={(e) => setModules((all) => all.map((m, i) => i === selection.moduleIndex
                    ? { ...m, topics: m.topics.map((t, j) => j === selection.topicIndex ? { ...t, estimatedMinutes: Number(e.target.value) } : t) }
                    : m))}
                />
                <BlockEditor
                  blocks={selectedTopic.blocks}
                  uploadContext={{
                    courseId,
                    catalogKind: 'courses',
                    entityName: title,
                    topicName: selectedTopic.title || 'topic',
                  }}
                  onChange={(blocks) => setModules((all) => all.map((m, i) => i === selection.moduleIndex
                    ? { ...m, linkOnly: false, topics: m.topics.map((t, j) => j === selection.topicIndex ? { ...t, blocks } : t) }
                    : m))}
                />
                <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-2">
                  <p className="text-sm font-medium text-gray-800">Reference links</p>
                  {(selectedTopic.references || []).map((ref, ri) => (
                    <div key={ri} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                      <input className="rounded-md border-gray-300 text-sm" placeholder="Title" value={ref.title} onChange={(e) => setModules((all) => all.map((m, i) => i === selection.moduleIndex ? {
                        ...m,
                        linkOnly: false,
                        topics: m.topics.map((t, j) => j === selection.topicIndex ? {
                          ...t,
                          references: (t.references || []).map((item, n) => n === ri ? { ...item, title: e.target.value } : item),
                        } : t),
                      } : m))} />
                      <input className="rounded-md border-gray-300 text-sm" placeholder="https://" value={ref.url} onChange={(e) => setModules((all) => all.map((m, i) => i === selection.moduleIndex ? {
                        ...m,
                        linkOnly: false,
                        topics: m.topics.map((t, j) => j === selection.topicIndex ? {
                          ...t,
                          references: (t.references || []).map((item, n) => n === ri ? { ...item, url: e.target.value } : item),
                        } : t),
                      } : m))} />
                      <Button type="button" variant="ghost" size="sm" onClick={() => setModules((all) => all.map((m, i) => i === selection.moduleIndex ? {
                        ...m,
                        topics: m.topics.map((t, j) => j === selection.topicIndex ? {
                          ...t,
                          references: (t.references || []).filter((_, n) => n !== ri),
                        } : t),
                      } : m))}>Remove</Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => setModules((all) => all.map((m, i) => i === selection.moduleIndex ? {
                    ...m,
                    linkOnly: false,
                    topics: m.topics.map((t, j) => j === selection.topicIndex ? {
                      ...t,
                      references: [...(t.references || []), { title: '', url: '', displayOrder: (t.references || []).length + 1 }],
                    } : t),
                  } : m))}>Add reference</Button>
                </div>
                <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-2">
                  <p className="text-sm font-medium text-gray-800">Labs on this topic</p>
                  <p className="text-xs text-gray-500">Labs added here belong to this course. They use the same category, course, module, and topic.</p>
                  {(selectedTopic.relatedLabs || []).length === 0 ? (
                    <p className="text-sm text-gray-400">No labs on this topic yet.</p>
                  ) : (
                    <ul className="text-sm text-gray-700 space-y-1">
                      {(selectedTopic.relatedLabs || []).map((lab) => (
                        <li key={lab.id} className="flex items-center justify-between gap-2">
                          <span>{lab.name}</span>
                          <span className="flex gap-2">
                            {lab.slug ? (
                              <button
                                type="button"
                                className="text-sm font-medium text-primary-600"
                                onClick={() => openLearnerTab(`/labs/${lab.slug}${courseSlug ? `?course=${courseSlug}` : ''}`)}
                              >
                                View
                              </button>
                            ) : null}
                            <Link to={`/admin/labs/${lab.id}/edit`} className="text-sm font-medium text-primary-600">Open lab</Link>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeLabFromTopic(selection.moduleIndex, selection.topicIndex, lab.id)}>Remove</Button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => openCreateLab(selection.moduleIndex, selection.topicIndex)}>
                      Create lab
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => openAddExistingLab(selection.moduleIndex, selection.topicIndex)}>
                      Add existing lab
                    </Button>
                  </div>
                  {!courseId || !selectedTopic.id ? (
                    <p className="text-xs text-gray-500">Save the course first to create a new lab under this topic. You can still link a lab that already exists.</p>
                  ) : null}
                  {labsOnThisCourse.filter((lab) => !(selectedTopic.relatedLabs || []).some((item) => item.id === lab.id)).length === 0 ? (
                    <p className="text-xs text-gray-500">No other labs on this course yet.</p>
                  ) : (
                    <select
                      className="w-full rounded-md border-gray-300 text-sm"
                      value=""
                      onChange={(e) => {
                        const lab = labsOnThisCourse.find((item) => item.id === Number(e.target.value));
                        if (lab) addLabToTopic(selection.moduleIndex, selection.topicIndex, lab);
                      }}
                    >
                      <option value="">Add an existing lab…</option>
                      {labsOnThisCourse
                        .filter((lab) => !(selectedTopic.relatedLabs || []).some((item) => item.id === lab.id))
                        .map((lab) => (
                          <option key={lab.id} value={lab.id}>{lab.name}</option>
                        ))}
                    </select>
                  )}
                  <button type="button" className="text-sm font-medium text-primary-600" onClick={() => setBrowse('lab')}>
                    Browse the catalog and link a lab
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 pt-6">
        {courseSlug ? (
          <Button variant="outline" type="button" onClick={() => openLearnerTab(`/courses/${courseSlug}`)}>View</Button>
        ) : null}
        <Button variant="outline" type="button" onClick={() => setPreview(!preview)}>{preview ? 'Edit' : 'Preview'}</Button>
        <Button variant="secondary" type="button" onClick={() => setConfirmAction('draft')} disabled={saving}>Save draft</Button>
        <Button type="button" onClick={() => setConfirmAction('review')} disabled={saving}>Submit for review</Button>
      </div>

      {aiPanel && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4"
          role="presentation"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <p className="text-lg font-semibold text-gray-900">
              {aiPanel === 'module' && 'Ask AI to add a module'}
              {aiPanel === 'topic' && 'Ask AI to add a topic'}
              {aiPanel === 'review' && 'Review and update this topic with AI'}
              {aiPanel === 'assessment' && 'Ask AI for an assessment'}
              {aiPanel === 'course' && 'Ask AI to create the entire course'}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {aiPanel === 'course'
                ? 'Describe the course, for example “Create an advanced Python course”. This page queues the work and fills in the course when it finishes. A long outline is grouped into a few topics per module. It can take a few minutes.'
                : 'Describe what you want generated. You can edit the result after it is added.'}
            </p>
            <div className="mt-4">
              <Input id="ai-topic" label="Focus" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} />
            </div>
            <label className="block text-sm font-medium text-gray-700">
              Extra instructions
              <textarea
                className="mt-1 w-full rounded-md border border-gray-300"
                rows={4}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={aiPanel === 'course' ? 'Create an advanced Python course' : 'Optional extra instructions'}
              />
            </label>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAiPanel(null)} disabled={generating}>Cancel</Button>
              <Button type="button" onClick={() => generate(
                aiPanel === 'course' ? 'entire-course' : aiPanel === 'assessment' ? 'assessment' : aiPanel === 'module' ? 'module' : 'topic',
                aiPanel === 'review' ? 'revise' : 'generate'
              )} disabled={generating}>
                {generating ? 'Generating…' : 'Generate'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <CatalogBrowseModal
        open={browse !== null}
        mode={browse === 'lab' ? 'lab' : 'module'}
        onClose={() => setBrowse(null)}
        onSelectModule={async (moduleId) => {
          try {
            const response = await contentApi.getModule(moduleId);
            const loaded = response.data.module;
            if (!loaded?.id || modules.some((item) => item.id === loaded.id)) return;
            const nextIndex = modules.length;
            setModules([...modules, {
              ...loaded,
              linkOnly: true,
              assessments: loaded.assessments || [],
              topics: (loaded.topics || []).map((topic) => ({
                ...topic,
                relatedLabs: topic.relatedLabs || [],
                references: topic.references || [],
                blocks: topic.blocks?.length ? topic.blocks : [emptyBlock(1)],
              })),
              displayOrder: nextIndex + 1,
            }]);
            setOpenTocModules((current) => current.includes(nextIndex) ? current : [...current, nextIndex]);
            selectModule(nextIndex, true);
            setBrowse(null);
          } catch {
            setError('Unable to add that module.');
          }
        }}
        onSelectLab={(lab) => {
          if (selection.kind === 'topic') {
            addLabToTopic(selection.moduleIndex, selection.topicIndex, {
              id: lab.id,
              name: lab.name,
              slug: lab.slug,
              difficulty: 'Beginner',
            });
            setModules((all) => all.map((m, i) => i === selection.moduleIndex ? { ...m, linkOnly: false } : m));
          }
          setBrowse(null);
        }}
      />
      <ConfirmChangesDialog
        open={confirmAction !== null}
        title={confirmAction === 'review' ? 'Submit for review?' : 'Save draft?'}
        lines={summarizeCourseChanges(previous as CourseSavePayload | null, payload)}
        confirmLabel={confirmAction === 'review' ? 'Submit for review' : 'Save draft'}
        busy={saving}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => save(confirmAction === 'review')}
      />
      {courseId ? <CourseEnrollmentAdminPanel courseId={courseId} courseTitle={title} /> : null}
      <UnsavedLeaveDialog open={leaveOpen} onStay={stay} onLeave={leaveWithoutSaving} />
    </div>
  );
};

export default CourseEditorPage;
